import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stethoscope, UserCheck, CalendarClock, DollarSign, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

export default function Overview() {
  const [doctors, setDoctors] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/hospital/doctors"),
      api.get("/hospital/appointments"),
    ]).then(([d, a]) => { setDoctors(d.data); setAppts(a.data); })
      .finally(() => setLoading(false));
  }, []);

  const todayStr = new Date().toISOString().slice(0, 10);
  const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const todays = appts.filter(a => (a.slot_start || "").slice(0, 10) === todayStr && a.status === "confirmed");
  const upcomingWeek = appts.filter(a => {
    const d = (a.slot_start || "").slice(0, 10);
    return d >= todayStr && d <= weekEnd && a.status === "confirmed";
  });

  // revenue this month: sum confirmed/completed fees (refunded/cancelled excluded)
  const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const monthIso = monthStart.toISOString();
  const revenue = appts
    .filter(a => ["confirmed", "completed"].includes(a.status) && (a.created_at || a.slot_start) >= monthIso)
    .reduce((sum, a) => sum + Number(a.consultation_fee || 0), 0);

  const total = doctors.length;
  const active = doctors.filter(d => d.is_active).length;
  const recent = [...doctors]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 5);

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ overview</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Today, at a glance.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Active doctors" value={loading ? "—" : `${active} / ${total}`} icon={<UserCheck className="h-4 w-4" />} testid="stat-active" />
        <Stat label="Today's appointments" value={loading ? "—" : todays.length} icon={<CalendarClock className="h-4 w-4" />} testid="stat-today" />
        <Stat label="Upcoming · 7 days" value={loading ? "—" : upcomingWeek.length} icon={<Stethoscope className="h-4 w-4" />} testid="stat-week" />
        <Stat label="Revenue · month" value={loading ? "—" : formatCurrency(revenue)} icon={<DollarSign className="h-4 w-4" />} testid="stat-revenue" />
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface" data-testid="recent-doctors">
        <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Recently added doctors</div>
            <div className="text-xs text-mb-fg2 mt-0.5">Last 5</div>
          </div>
          <Link to="/hospital/doctors" className="text-xs text-teal-400 hover:text-teal-300 inline-flex items-center gap-1">
            Manage all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <ul className="divide-y divide-mb-border">
          {recent.length === 0 && (
            <li className="px-6 py-8 text-sm text-mb-fg2 text-center">{loading ? "Loading…" : "No doctors yet."}</li>
          )}
          {recent.map((d) => (
            <li key={d.id} className="px-6 py-4 flex items-center gap-4">
              <div className="h-9 w-9 rounded-full bg-teal-500/10 text-teal-300 flex items-center justify-center text-sm font-medium">
                {(d.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{d.full_name}</div>
                <div className="text-xs text-mb-fg2 truncate">{d.specialty} · {d.email}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${
                d.is_active ? "bg-teal-500/10 text-teal-300 border border-teal-500/20" : "bg-zinc-800 text-mb-fg2"
              }`}>{d.is_active ? "active" : "inactive"}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-mb-border bg-mb-surface p-5">
      <div className="flex items-center justify-between text-mb-fg2">
        <span className="text-[10px] uppercase tracking-[0.25em]">{label}</span>
        <span className="text-teal-400">{icon}</span>
      </div>
      <div className="mt-3 text-3xl font-light text-white">{value}</div>
    </div>
  );
}
